import '@ui5/webcomponents-icons/dist/search.js';
import { enrichEventWithDetails, useI18nBundle } from '@ui5/webcomponents-react-base';
import React, { Children, cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { BarDesign } from '../../enums/BarDesign';
import { ButtonDesign } from '../../enums/ButtonDesign';
import { FlexBoxAlignItems } from '../../enums/FlexBoxAlignItems';
import { FlexBoxDirection } from '../../enums/FlexBoxDirection';
import { FlexBoxJustifyContent } from '../../enums/FlexBoxJustifyContent';
import { TitleLevel } from '../../enums/TitleLevel';
import { BASIC, CANCEL, CLEAR, FILTERS, GO, RESTORE, SAVE, SEARCH_FOR_FILTERS, SHOW_ON_FILTER_BAR } from '../../i18n/i18n-defaults';
import { stopPropagation } from '../../internal/stopPropagation';
import { Bar } from '../../webComponents/Bar';
import { Button } from '../../webComponents/Button';
import { CheckBox } from '../../webComponents/CheckBox';
import { Dialog } from '../../webComponents/Dialog';
import { Icon } from '../../webComponents/Icon';
import { Input } from '../../webComponents/Input';
import { Title } from '../../webComponents/Title';
import { FlexBox } from '../FlexBox';
import { Text } from '../Text';
import styles from './FilterBarDialog.jss';
import { filterValue, renderSearchWithValue, syncRef } from './utils';
const useStyles = createUseStyles(styles, { name: 'FilterBarDialog' });
export const FilterDialog = (props) => {
    const { filterBarRefs, open, handleDialogClose, children, showClearButton, showRestoreButton, showGoButton, showSearch, renderFBSearch, handleClearFilters, handleRestoreFilters, handleDialogSave, searchValue, handleSearchValueChange, onGo, handleSelectionChange, handleDialogSearch, handleDialogCancel, portalContainer, dialogRef } = props;
    const classes = useStyles();
    const [searchString, setSearchString] = useState('');
    const searchRef = useRef(null);
    const [toggledFilters, setToggledFilters] = useState({});
    const dialogRefs = useRef({});
    const dialogSearchRef = useRef(null);
    const i18nBundle = useI18nBundle('@ui5/webcomponents-react');
    const basicText = i18nBundle.getText(BASIC);
    const cancelText = i18nBundle.getText(CANCEL);
    const clearText = i18nBundle.getText(CLEAR);
    const restoreText = i18nBundle.getText(RESTORE);
    const saveText = i18nBundle.getText(SAVE);
    const searchForFiltersText = i18nBundle.getText(SEARCH_FOR_FILTERS);
    const showOnFilterBarText = i18nBundle.getText(SHOW_ON_FILTER_BAR);
    const filtersTitle = i18nBundle.getText(FILTERS);
    const goText = i18nBundle.getText(GO);
    useEffect(() => {
        if (open) {
            dialogRef.current.show();
        }
    }, [open]);
    const handleSearch = (e) => {
        if (handleDialogSearch) {
            handleDialogSearch(enrichEventWithDetails(e, { value: e.target.value, element: e.target }));
        }
        setSearchString(e.target.value);
    };
    const handleSave = (e, go = false) => {
        var _a;
        if (renderFBSearch) {
            handleSearchValueChange((_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.children[1].value);
        }
        if (go) {
            handleDialogSave(e, dialogRefs.current, toggledFilters, true);
        }
        else {
            handleDialogSave(e, dialogRefs.current, toggledFilters);
        }
    };
    const handleClose = (e) => {
        stopPropagation(e);
        if (!showGoButton) {
            handleSave(e);
            return;
        }
        handleDialogClose(e);
    };
    const handleDialogGo = (e) => {
        if (typeof onGo === 'function') {
            handleSave(e, true);
        }
    };
    const handleCancel = (e) => {
        if (handleDialogCancel) {
            handleDialogCancel(enrichEventWithDetails(e));
        }
        handleDialogClose(e);
    };
    const getFilterElements = () => {
        var _a;
        const search = (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-component-name="FilterBarSearch"]`);
        return {
            filters: dialogFilterRefs.current,
            search,
            dialogSearch: dialogSearchRef.current
        };
    };
    const handleRestore = (e) => {
        handleRestoreFilters(e, 'dialog', getFilterElements());
    };
    const handleClear = (e) => {
        if (typeof handleClearFilters === 'function') {
            handleClearFilters(enrichEventWithDetails(e, getFilterElements()));
        }
    };
    const renderChildren = () => {
        return children
            .filter((item) => {
            var _a, _b, _c;
            return (!!(item === null || item === void 0 ? void 0 : item.props) &&
                ((_a = item.props) === null || _a === void 0 ? void 0 : _a.visible) &&
                (((_c = (_b = item.props) === null || _b === void 0 ? void 0 : _b.label) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes(searchString.toLowerCase())) || searchString.length === 0));
        })
            .map((child) => {
            const filterBarItemRef = filterBarRefs.current[child.key];
            let filterItemProps = {};
            if (filterBarItemRef) {
                filterItemProps = filterValue(filterBarItemRef, child);
            }
            if (!child.props.children)
                return child;
            return cloneElement(child, {
                children: Object.assign(Object.assign({}, child.props.children), { props: Object.assign(Object.assign({}, child.props.children.props), filterItemProps), ref: (node) => {
                        if (node) {
                            dialogRefs.current[child.key] = node;
                            syncRef(child.props.children.ref, node);
                        }
                    } })
            });
        });
    };
    const handleCheckBoxChange = useCallback((element) => (e) => {
        if (handleSelectionChange) {
            handleSelectionChange(enrichEventWithDetails(e, { element, checked: e.target.checked }));
        }
        setToggledFilters((old) => (Object.assign(Object.assign({}, old), { [element.key]: e.target.checked })));
    }, [setToggledFilters, handleSelectionChange]);
    const dialogFilterRefs = useRef([]);
    const renderGroups = () => {
        const groups = {};
        const dialogFilters = [];
        Children.forEach(renderChildren(), (child) => {
            var _a;
            const childGroups = (_a = child.props.groupName) !== null && _a !== void 0 ? _a : 'default';
            if (groups[childGroups]) {
                groups[childGroups].push(child);
            }
            else {
                groups[childGroups] = [child];
            }
        });
        const filterGroups = Object.keys(groups)
            .sort((x, y) => (x === 'default' ? -1 : y === 'role' ? 1 : 0))
            .map((item, index) => {
            const filters = groups[item].map((el) => {
                return (React.createElement("div", { "data-component-name": "FilterDialogSingleFilterContainer", className: classes.singleFilter, key: `${el.key}-container`, ref: (node) => {
                        var _a, _b, _c, _d, _e;
                        if (node) {
                            dialogFilters.push((_e = (_d = (_c = (_b = (_a = node.children) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.children) === null || _e === void 0 ? void 0 : _e[1]);
                        }
                    } },
                    el,
                    React.createElement(CheckBox, { role: "checkbox", checked: el.props.visibleInFilterBar || el.props.required || el.type.displayName !== 'FilterGroupItem', onChange: handleCheckBoxChange(el), disabled: el.props.required || el.type.displayName !== 'FilterGroupItem' })));
            });
            return (React.createElement("div", { className: classes.groupContainer, key: item },
                React.createElement(FlexBox, { justifyContent: FlexBoxJustifyContent.SpaceBetween, alignItems: FlexBoxAlignItems.Center },
                    React.createElement(Title, { level: TitleLevel.H5, className: index === 0 ? classes.groupTitle : '', title: item === 'default' ? basicText : item }, item === 'default' ? basicText : item),
                    index === 0 && React.createElement(Text, { wrapping: false }, showOnFilterBarText)),
                React.createElement("div", { className: classes.filters }, filters)));
        });
        dialogFilterRefs.current = dialogFilters;
        return filterGroups;
    };
    return createPortal(React.createElement(Dialog, { className: classes.dialogComponent, ref: dialogRef, header: React.createElement(FlexBox, { direction: FlexBoxDirection.Column, alignItems: FlexBoxAlignItems.Center, className: classes.header },
            React.createElement(Title, { level: TitleLevel.H4, title: filtersTitle }, filtersTitle),
            showSearch && (React.createElement(Input, { placeholder: searchForFiltersText, onInput: handleSearch, icon: React.createElement(Icon, { name: "search" }), ref: dialogSearchRef }))), footer: React.createElement(Bar, { design: BarDesign.Footer, endContent: React.createElement(FlexBox, { justifyContent: FlexBoxJustifyContent.End, className: classes.footer },
                showGoButton && (React.createElement(Button, { onClick: handleDialogGo, design: ButtonDesign.Emphasized, title: goText, "data-component-name": "FilterBarDialogGoBtn" }, goText)),
                React.createElement(Button, { onClick: handleSave, "data-component-name": "FilterBarDialogSaveBtn", design: showGoButton ? ButtonDesign.Transparent : ButtonDesign.Emphasized }, saveText),
                showClearButton && (React.createElement(Button, { onClick: handleClear, "data-component-name": "FilterBarDialogClearBtn" }, clearText)),
                showRestoreButton && (React.createElement(Button, { onClick: handleRestore, "data-component-name": "FilterBarDialogRestoreBtn" }, restoreText)),
                React.createElement(Button, { design: ButtonDesign.Transparent, onClick: handleCancel, "data-component-name": "FilterBarDialogCancelBtn" }, cancelText)) }), onAfterClose: handleClose, "data-component-name": "FilterBarDialog" },
        React.createElement("div", { className: classes.dialog, "data-component-name": "FilterBarDialogContent" },
            renderFBSearch && (React.createElement("div", { className: classes.fbSearch, ref: searchRef },
                React.createElement("span", null),
                renderSearchWithValue(renderFBSearch, searchValue))),
            renderGroups())), portalContainer);
};
